import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { Transition } from '@headlessui/react';
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import { HiXMark } from 'react-icons/hi2'; // for a close icon
import { motion, AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';
import { ProductsContext } from '../context/products.context';
import RangeHistogramSlider from './RangeHistogramSlider';
import DiscreteRangeSlider from './DiscreteRangeSlider';
import { usePlucks, useDebouncedAdvancedFilterUpdate } from '../hooks/usePlucks';
import EducationFilter from './EducationFilter';

//Helper: onClickOutside – closes panel when clicking outside.
function useOnClickOutside(refs, handler) {
  useEffect(() => {
    const listener = (e) => {
      for (const ref of refs) {
        if (!ref.current) continue;
        if (ref.current.contains(e.target)) return;
      }
      handler(e);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [refs, handler]);
}

// Formatting helpers
function formatCurrency(v) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    notation: 'compact',
    compactDisplay: 'short'
  }).format(v);
}
function formatNumber(v) {
  return new Intl.NumberFormat('en-US').format(v);
}

export default function AdvancedFilterPanel({ openingsLookup, growthLookup }) {
  const {
    advancedFilters,
    setAdvancedFilters,
    histogramParams,
    originalSalaryRange,
    workforce,
    globalSalaryData,
    jobs,
    isOpen,
    setIsOpen
  } = useContext(ProductsContext);

  // Panel open/close state and refs for onClickOutside
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  useOnClickOutside([panelRef, buttonRef], () => {
    if (isOpen) setIsOpen(false);
  });

  // --- Salary Slider (RangeHistogramSlider) ---
  const [selectedSalaryRange, setSelectedSalaryRange] = useState(originalSalaryRange);
  useEffect(() => {
    setSelectedSalaryRange(originalSalaryRange);
  }, [originalSalaryRange, workforce]);
  const debouncedUpdateSalary = useCallback(
    debounce((newRange) => {
      setAdvancedFilters((prev) => ({ ...prev, salaryRange: newRange }));
    }, 300),
    [setAdvancedFilters]
  );
  const handleSalaryChange = (newRange) => {
    setSelectedSalaryRange(newRange);
    debouncedUpdateSalary(newRange);
  };

  // --- Openings and Growth Discrete Sliders ---
  const openingsPlucks = usePlucks(jobs, 'totalOpeningsRating', 'avgTotalOpenings', 1);
  const growthPlucks = usePlucks(jobs, 'growthRating', 'longtermGrowth', 1);
  const [openingsRange, setOpeningsRange] = useState([1, 5]);
  const [growthRange, setGrowthRange] = useState([1, 5]);

  const defaultOpenings = useMemo(() => {
    return openingsPlucks.length === 5 && openingsLookup && openingsLookup.length
      ? [openingsPlucks[0].range[0], openingsPlucks[4].range[1]]
      : [openingsLookup[0], openingsLookup[openingsLookup.length - 1]];
  }, [openingsPlucks, openingsLookup]);

  const defaultGrowth = useMemo(() => {
    return growthPlucks.length === 5 && growthLookup && growthLookup.length
      ? [growthPlucks[0].range[0], growthPlucks[4].range[1]]
      : [growthLookup[0], growthLookup[growthLookup.length - 1]];
  }, [growthPlucks, growthLookup]);

  useEffect(() => {
    if (openingsLookup && openingsLookup.length) {
      setOpeningsRange([1, 5]);
      setAdvancedFilters((prev) => ({
        ...prev,
        openingsRange: defaultOpenings,
      }));
    }
  }, [openingsPlucks, openingsLookup, defaultOpenings, setAdvancedFilters]);

  useEffect(() => {
    if (growthPlucks.length === 5 && growthLookup && growthLookup.length) {
      setGrowthRange([1, 5]);
      setAdvancedFilters((prev) => ({
        ...prev,
        growthRange: defaultGrowth,
      }));
    }
  }, [growthPlucks, growthLookup, defaultGrowth, setAdvancedFilters]);

  const debouncedUpdateOpenings = useDebouncedAdvancedFilterUpdate(
    openingsPlucks,
    'openingsRange',
    setAdvancedFilters,
    300
  );
  const debouncedUpdateGrowth = useDebouncedAdvancedFilterUpdate(
    growthPlucks,
    'growthRange',
    setAdvancedFilters,
    300
  );
  const handleOpeningsChange = (newRange) => {
    setOpeningsRange(newRange);
    debouncedUpdateOpenings(newRange);
  };
  const handleGrowthChange = (newRange) => {
    setGrowthRange(newRange);
    debouncedUpdateGrowth(newRange);
  };

  // --- Active Filter Pills ---
  const activePills = [];
  if (
    advancedFilters.salaryRange[0] !== originalSalaryRange[0] ||
    advancedFilters.salaryRange[1] !== originalSalaryRange[1]
  ) {
    activePills.push({
      key: 'salaryRange',
      label: `Salary: ${formatCurrency(advancedFilters.salaryRange[0])} - ${formatCurrency(
        advancedFilters.salaryRange[1]
      )}`,
    });
  }
  if (
    advancedFilters.openingsRange &&
    defaultOpenings &&
    (advancedFilters.openingsRange[0] !== defaultOpenings[0] ||
      advancedFilters.openingsRange[1] !== defaultOpenings[1])
  ) {
    activePills.push({
      key: 'openingsRange',
      label: `Openings: ${formatNumber(advancedFilters.openingsRange[0])} - ${formatNumber(
        advancedFilters.openingsRange[1]
      )}`,
    });
  }
  if (
    advancedFilters.growthRange[0] !== growthLookup[0] ||
    advancedFilters.growthRange[1] !== growthLookup[growthLookup.length - 1]
  ) {
    activePills.push({
      key: 'growthRange',
      label: `Growth: ${(advancedFilters.growthRange[0] * 100).toFixed(0)}% - ${(
        advancedFilters.growthRange[1] * 100
      ).toFixed(0)}%`,
    });
  }
  if (advancedFilters.hasCipOffered) {
    activePills.push({ key: 'hasCipOffered', label: 'Has CIP Offered' });
  }
  if (advancedFilters.isStem) {
    activePills.push({ key: 'isStem', label: 'Is STEM' });
  }
  if (advancedFilters.education.length) {
    advancedFilters.education.forEach((ed) => {
      activePills.push({ key: `education-${ed}`, label: ed });
    });
  }
  function handleRemovePill(pill) {
    if (pill.key === 'salaryRange') {
      setAdvancedFilters((prev) => ({ ...prev, salaryRange: originalSalaryRange }));
      setSelectedSalaryRange(originalSalaryRange);
    } else if (pill.key === 'openingsRange') {
      setAdvancedFilters((prev) => ({ ...prev, openingsRange: defaultOpenings }));
      setOpeningsRange([1, 5]);
    } else if (pill.key === 'growthRange') {
      setAdvancedFilters((prev) => ({ ...prev, growthRange: defaultGrowth }));
      setGrowthRange([1, 5]);
    } else if (pill.key === 'hasCipOffered') {
      setAdvancedFilters((prev) => ({ ...prev, hasCipOffered: false }));
    } else if (pill.key === 'isStem') {
      setAdvancedFilters((prev) => ({ ...prev, isStem: false }));
    } else if (pill.key.startsWith('education-')) {
      const edLabel = pill.key.replace('education-', '');
      setAdvancedFilters((prev) => ({
        ...prev,
        education: prev.education.filter((e) => e !== edLabel),
      }));
    }
  }
  function handleClearAll() {
    setAdvancedFilters({
      education: [],
      salaryRange: originalSalaryRange,
      openingsRange: defaultOpenings,
      growthRange: defaultGrowth,
      hasCipOffered: false,
      isStem: false,
    });
    setSelectedSalaryRange(originalSalaryRange);
    setOpeningsRange([1, 5]);
    setGrowthRange([1, 5]);
  }

  return (
    <div className="relative select-none">
      {/* Top Bar: Filters button and active pills */}
      <div className="flex items-start space-x-2">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center px-3 py-2  text-gray-700 rounded-lg bg-zinc-100 border-transparent border border-zinc-300 hover:border-zinc-500 hover:bg-zinc-200"
        >
          <HiAdjustmentsHorizontal className="mr-2" />
          Filters
        </button>
        <AnimatePresence>
          {activePills.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {activePills.map((pill) => (
                <motion.button
                  key={pill.key}
                  onClick={() => handleRemovePill(pill)}
                  className="group flex items-center px-3 py-2 font-light bg-blue-100/60 text-black rounded-md"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  {pill.label}
                  <span className="ml-2 cursor-pointer font-bold group-hover:text-blue-700">
                    ×
                  </span>
                </motion.button>
              ))}
              <motion.button
                onClick={handleClearAll}
                className="text-blue-500 no-underline hover:underline p-2 m-1 text-xs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                CLEAR ALL
              </motion.button>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Slide-Over / Sidebar Panel */}
      <Transition
        show={isOpen}
        enter="transition duration-300 ease-out"
        enterFrom="opacity-0 -translate-x-2"
        enterTo="opacity-100 translate-x-0"
        leave="transition duration-200 ease-in"
        leaveFrom="opacity-100 translate-x-0"
        leaveTo="opacity-0 -translate-x-2"
      >
        <div
          ref={panelRef}
          className="
        absolute -top-44 -left-[420px] z-50 bg-white  shadow-md border p-4 px-12 rounded w-[420px] space-y-2"
          // If you'd like it full height and sticky, you can do "fixed top-0 left-0 h-full"
        >
{/* Header (with Title + Close Button) */}
<div className="flex justify-between items-center h-14 mb-4 bg-zinc-100 -mx-12 -mt-4 overflow-hidden bg-gradient-b pr-4 border-t border-zinc-200">
  <h3 className="text-base text-center text-zinc-800 w-full font-normal pl-4">Filters</h3>
  <button
    onClick={() => setIsOpen(false)}
    className={`
      inline-flex items-center justify-center
      w-10 h-10 p-2
      rounded-full border-2 border-transparent
      hover:bg-white-200 hover:border-zinc-500 hover:bg-zinc-200
      focus:outline-none focus:ring-2 focus:ring-blue-500
    `}
    aria-label="Close Filter Panel"
  >
    <HiXMark className="w-6 h-6 text-gray-600" />
  </button>
</div>

          {/* Salary Filter */}
          <section>
            <RangeHistogramSlider
              label="Salary Range"
              min={originalSalaryRange[0]}
              max={originalSalaryRange[1]}
              binSize={histogramParams.salaryBinSize}
              histogramData={globalSalaryData}
              selectedRange={selectedSalaryRange}
              formatValue={formatCurrency}
              onValueChange={handleSalaryChange}
            />
          </section>
          <hr />

          {/* Openings Slider */}
          <section>
            {openingsPlucks.length === 5 ? (
              <DiscreteRangeSlider
                label="# of Openings"
                customLabels={['Very Low', 'Low', 'Moderate', 'High', 'Very High']}
                ratingValues={openingsPlucks}
                value={openingsRange}
                onValueChange={handleOpeningsChange}
              />
            ) : (
              <p className="text-sm text-red-500">
                No data or insufficient data to build openings slider.
              </p>
            )}
          </section>
          <hr />

          {/* Growth Slider */}
          <section>
            {growthPlucks.length === 5 ? (
              <DiscreteRangeSlider
                label="% Growth"
                ratingValues={growthPlucks}
                value={growthRange}
                customLabels={['Very Slow', 'Below Avg', 'Average', 'Above Avg', 'Strong']}
                onValueChange={handleGrowthChange}
              />
            ) : (
              <p className="text-sm text-red-500">
                No data or insufficient data to build growth slider.
              </p>
            )}
          </section>
          <hr />

          {/* Education Filter */}
          <section>
            <EducationFilter
              education={advancedFilters.education}
              setEducation={(newEducation) =>
                setAdvancedFilters((prev) => ({ ...prev, education: newEducation }))
              }
            />
          </section>
          <hr />

          {/* Additional Toggle Buttons */}
          <section>
            <div className="flex items-center mb-2 space-x-2">
              <input
                type="checkbox"
                checked={advancedFilters.hasCipOffered}
                onChange={() =>
                  setAdvancedFilters((prev) => ({
                    ...prev,
                    hasCipOffered: !prev.hasCipOffered,
                  }))
                }
              />
              <span>Has CIP enrollments?</span>
            </div>
            <div className="flex items-center mb-4 space-x-2">
              {/* <input
                type="checkbox"
                checked={advancedFilters.isStem}
                onChange={() =>
                  setAdvancedFilters((prev) => ({ ...prev, isStem: !prev.isStem }))
                }
              />
              <span>Is STEM?</span> */}
            </div>
          </section>

          {/* Clear / Apply Buttons */}
          <div className="flex justify-between mt-4">
            <button
              onClick={handleClearAll}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear All
            </button>
            {/* If you wanted an explicit "Apply" button instead of immediate updates: */}
            {/* <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button> */}
          </div>
        </div>
      </Transition>
    </div>
  );
}